import useUsers from "@/hooks/useUsers";
import { User } from "@/models/users/User";
import { SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";

interface UseOrganizationUsersTableDataReturn {
  data: User[];
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  isLoading: boolean;
}

const useOrganizationUsersTableData = (
  initialPageSize: number = 20
): UseOrganizationUsersTableDataReturn => {
  // Hooks
  const { useQueryPagination: userPagination } = useUsers();

  // Data state
  const [data, setData] = useState<User[]>([]);
  const [pageCount, setPageCount] = useState(0);

  // Pagination state
  const [pageIndex, setPageIndex] = useState(0); // zero-based index
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Fetch data
  const fetchedUsers = userPagination({
    pageNumber: pageIndex + 1, // Convert zero-based index to one-based page number
    pageSize,
    sortAscending: sorting.length ? !sorting[0].desc : false,
    sortBy: sorting.length ? sorting[0].id : "createdAt",
  });

  // Effects
  useEffect(() => {
    if (fetchedUsers.isFetched && fetchedUsers.data && fetchedUsers.data.data) {
      setData(fetchedUsers.data.data);
      setPageCount(fetchedUsers.data.totalPages);
    }
  }, [fetchedUsers.data, fetchedUsers.isFetched]);

  return {
    data,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setSorting,
    setPageIndex,
    setPageSize,
    isLoading: fetchedUsers.isLoading,
  };
};

export default useOrganizationUsersTableData;
